import { ApplicationRef, Component, computed, DestroyRef, effect, ElementRef, HostListener, inject, signal, viewChild } from '@angular/core';
import { NavigationEnd, Router, RouteReuseStrategy, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs/operators';

import { PlaygroundQueryPage } from './utils/playground-query-page';
import { PlaygroundReuseStrategy } from './utils/playground-reuse-strategy';
import { RELEASED_RESULT_VIEWS, ResultView } from './utils/result-views';
import { ENDPOINT_INTROS } from './utils/endpoint-intros';
import { startPlaygroundTour } from './utils/playground-tour';
import { TourProgressService } from './utils/tour-progress';
import { PlaygroundKeyService } from './utils/playground-key.service';
import { getEndpointDefinition } from '../../core/models/endpoint-catalog.model';
import { Driver } from 'driver.js';
import { EndpointCatalogService, SessionService } from '../../core/services';
import { ModalService } from '../../shared/modal';
import { BillingService } from '../billing/services/billing.service';
import { CODE_LANGUAGES } from './utils/code-snippet';
import { RagResult } from './models/rag-result.model';
import { SearchResult } from './models/search-result.model';
import { AnswerResult } from './models/answer-result.model';
import { ImageResult } from './models/image-result.model';
import { PHONE_QUERY } from '../../core/utils/phone';

export type ResponseTab = 'response' | 'json' | 'headers';
export type WorkspaceLayout = 'stacked' | 'side';
/** Phones show one panel at a time, picked from these tabs. */
export type PhoneTab = 'request' | 'response' | 'code';

interface WorkspacePrefs {
  layout: WorkspaceLayout;
  codeCollapsed: boolean;
  ratio: Record<WorkspaceLayout, number>;
}

const PREFS_KEY = 'mila.playground.workspace';
const DEFAULT_PREFS: WorkspacePrefs = { layout: 'stacked', codeCollapsed: false, ratio: { stacked: 0.42, side: 0.46 } };
/** Below this workspace width, side by side would squeeze both panels. */
const SIDE_MIN_WIDTH = 900;

function loadPrefs(): WorkspacePrefs {
  try {
    const raw = JSON.parse(localStorage.getItem(PREFS_KEY) ?? 'null');
    if (!raw) return DEFAULT_PREFS;
    return {
      layout: raw.layout === 'side' ? 'side' : 'stacked',
      codeCollapsed: !!raw.codeCollapsed,
      ratio: {
        stacked: typeof raw.ratio?.stacked === 'number' ? raw.ratio.stacked : DEFAULT_PREFS.ratio.stacked,
        side: typeof raw.ratio?.side === 'number' ? raw.ratio.side : DEFAULT_PREFS.ratio.side,
      },
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

@Component({
  selector: 'app-playground',
  standalone: false,
  templateUrl: './playground.html',
  styleUrl: './playground.scss',
})
export class Playground {
  activeChild: PlaygroundQueryPage | null = null;

  // ── Workspace: request code and response as two independent panels ──
  private readonly prefs = loadPrefs();
  readonly layout = signal<WorkspaceLayout>(this.prefs.layout);
  readonly codeCollapsed = signal(this.prefs.codeCollapsed);
  readonly stackedRatio = signal(this.prefs.ratio.stacked);
  readonly sideRatio = signal(this.prefs.ratio.side);
  /** The workspace is too narrow for side by side (then it's always stacked). */
  readonly narrow = signal(false);
  readonly effectiveLayout = computed<WorkspaceLayout>(() => (this.narrow() ? 'stacked' : this.layout()));

  // ── Phone: one panel at a time ──
  private readonly phoneMedia = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(PHONE_QUERY) : null;
  readonly isPhone = signal(this.phoneMedia?.matches ?? false);
  /** The open tab; every endpoint opens on Request. */
  readonly phoneTab = signal<PhoneTab>('request');
  /** A run finished while another tab was open: Response shows a dot. */
  readonly responseUnseen = signal(false);
  /** The "⋯" menu in the phone run bar (holds Reset). */
  readonly runMenuOpen = signal(false);
  /** The key every run is billed to, picked in the run bar for all endpoints. */
  protected readonly apiKey = inject(PlaygroundKeyService);
  readonly keyMenuOpen = signal(false);
  /** Loaded and there's no active key: Run stays off, the bar links to create one. */
  readonly noApiKey = computed(() => this.apiKey.loaded() && !this.apiKey.keys().length);
  /** The code panel is folded to its header; never on phones, where the
   *  Code tab is the code. */
  readonly codeFolded = computed(() => !this.isPhone() && this.codeCollapsed());
  private readonly phoneTabs = viewChild<ElementRef<HTMLElement>>('phoneTabs');
  private readonly area = viewChild<ElementRef<HTMLElement>>('area');

  private readonly sideEl = viewChild<ElementRef<HTMLElement>>('side');
  private readonly formScroll = viewChild<ElementRef<HTMLElement>>('formScroll');
  /** More of the form below the fold: the run bar casts a shadow up over it. */
  readonly formMore = signal(false);
  /** The form's scrollbar width; the run bar stops short by it so its edges
   *  line up with the cards (0 with overlay scrollbars). */
  readonly formGutter = signal(0);
  private formObserver: ResizeObserver | null = null;
  private readonly responseBody = viewChild<ElementRef<HTMLElement>>('responseBody');
  private readonly outlet = viewChild(RouterOutlet);
  private readonly router = inject(Router);

  /** "/rag", "/answer"… for the code panel's header. */
  readonly endpointPath = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => endpointPathOf(this.router.url)),
    ),
    { initialValue: endpointPathOf(this.router.url) },
  );

  constructor(
    private modalService: ModalService,
    private session: SessionService,
    private billingService: BillingService,
  ) {
    // Remember layout, collapsed state and divider positions per browser.
    effect(() => {
      const prefs: WorkspacePrefs = {
        layout: this.layout(),
        codeCollapsed: this.codeCollapsed(),
        ratio: { stacked: this.stackedRatio(), side: this.sideRatio() },
      };
      try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch { /* private mode etc. */ }
    });

    // Watch the workspace's width so side by side is only offered when it fits.
    let observer: ResizeObserver | null = null;
    effect(() => {
      observer?.disconnect();
      const el = this.sideEl()?.nativeElement;
      if (!el || typeof ResizeObserver === 'undefined') return;
      observer = new ResizeObserver(([entry]) => this.narrow.set(entry.contentRect.width < SIDE_MIN_WIDTH));
      observer.observe(el);
    });
    // For "4 min ago" on a result you come back to.
    const clock = setInterval(() => this.now.set(Date.now()), 30_000);

    // Phone layout on and off as the window crosses the breakpoint.
    const onMedia = (e: MediaQueryListEvent) => this.isPhone.set(e.matches);
    this.phoneMedia?.addEventListener('change', onMedia);

    this.apiKey.load().pipe(takeUntilDestroyed()).subscribe();

    // The endpoint pages kept alive while switching go with the Playground.
    const reuse = inject(RouteReuseStrategy);
    inject(DestroyRef).onDestroy(() => {
      observer?.disconnect();
      this.phoneMedia?.removeEventListener('change', onMedia);
      this.formObserver?.disconnect();
      this.stopElapsed();
      this.endTour();
      clearInterval(clock);
      this.runTimes.clear();
      if (reuse instanceof PlaygroundReuseStrategy) reuse.clear();
    });
  }

  /** Collapsed stays collapsed: both layouts fold the code to its header. */
  setLayout(layout: WorkspaceLayout): void {
    this.layout.set(layout);
  }

  toggleCode(): void {
    this.codeCollapsed.update(v => !v);
  }

  /** A click on the code panel's header, anywhere but its own controls,
   *  collapses/expands it too (the ⌄ button stays the keyboard way in).
   *  A drag that selected text isn't a click. */
  onCodeHeadClick(event: MouseEvent): void {
    if (this.isPhone()) return;
    if ((event.target as Element).closest('button, a, input, select, [role="tab"]')) return;
    if (window.getSelection()?.toString()) return;
    this.toggleCode();
  }

  /** The account's currency code (e.g. "EUR"), for labelling the
   *  Playground's price estimate the estimate itself (see
   *  playground/utils/endpoint-pricing.ts) is already in cents no
   *  wallet-rate conversion needed only the label comes from the wallet,
   *  so it stays `null` while that's still loading. */
  readonly currency = computed(() => this.billingService.wallet()?.wallet.currency ?? null);

  /** /image, /similar, /pages, /legal aren't finished their tabs
   *  (playground.html) stay clickable only for superadmin, who can also
   *  still reach them by URL (see app-routing-module.ts's
   *  superadminGuard on those exact child routes); everyone else sees
   *  them dimmed and disabled. */
  get isSuperadmin(): boolean {
    return !!this.session.user?.superadmin;
  }

  readonly codeLanguages = CODE_LANGUAGES;

  readonly findEndpoints = [
    { path: '/rag', link: '/playground/rag', hint: 'Passages that answer a question' },
    { path: '/answer', link: '/playground/answer', hint: 'A written answer, with sources' },
    { path: '/search', link: '/playground/search', hint: 'Whole articles for a Boolean query' },
    { path: '/image', link: '/playground/image', hint: 'Images for a query' },
    { path: '/similar', link: '/playground/similar', hint: 'Articles similar to a query' },
  ];
  readonly fetchEndpoints = [
    { path: '/pages', link: '/playground/pages', hint: 'Fetch one web page' },
    { path: '/legal', link: '/playground/legal', hint: 'Legal terms of a domain' },
  ];

  /** One-line description of the open endpoint, shown at the end of the switcher. */
  readonly endpointHint = computed(() =>
    [...this.findEndpoints, ...this.fetchEndpoints].find(e => e.path === this.endpointPath())?.hint ?? '',
  );
  /** The open endpoint's words for its tour; null for one without a tour. */
  readonly intro = computed(() => ENDPOINT_INTROS[this.endpointPath()] ?? null);

  private tour: Driver | null = null;
  private readonly tourProgress = inject(TourProgressService);
  private readonly catalog = inject(EndpointCatalogService).catalog;

  /** The open endpoint's description from GET /endpoints (its API reference
   *  text), for the tour's first card; null until the catalog has loaded. */
  readonly endpointDescription = computed(() =>
    getEndpointDefinition(this.catalog(), this.endpointPath().slice(1))?.description ?? null,
  );
  private readonly appRef = inject(ApplicationRef);

  /** The walkthrough of the open endpoint's page (utils/playground-tour.ts). */
  /** The first tour is about to start: the loader in the middle shows. */
  readonly tourLoading = signal(false);
  /** The loader's 12 spokes. */
  readonly tourSpokes = Array.from({ length: 12 }, (_, i) => i);

  /** This endpoint's first visit, and its tour not taken: the button says "New". */
  readonly tourNew = signal(false);
  /** The pop-up under the Tour button offering this page's tour (first visit only). */
  readonly tourHint = signal(false);

  /** "Not now" / ✕: the pop-up goes; the dot stays until the page is left. */
  dismissTourHint(): void {
    this.tourHint.set(false);
  }

  startTour(): void {
    const intro = this.intro();
    const page = this.activeChild;
    if (!intro || !page) return;
    this.tourNew.set(false);
    this.tourHint.set(false);
    this.tour?.destroy();
    const path = this.endpointPath();
    const phone = this.isPhone();
    // The phone tour starts in the form, at the top of the page.
    if (phone) {
      this.setPhoneTab('request');
      // Rendered now, so the first steps find the form on screen.
      try { this.appRef.tick(); } catch { /* already rendering; it shows on its own */ }
    }
    this.tour = startPlaygroundTour({
      path,
      intro,
      phone,
      isMac: this.isMac,
      useExample: page.useExample ? text => page.useExample!(text) : undefined,
      openDocs: url => this.router.navigateByUrl(url),
      onStart: () => this.tourProgress.markSeen(path),
      description: this.endpointDescription() ?? undefined,
      codePanel: { collapsed: () => this.codeCollapsed(), setCollapsed: v => this.codeCollapsed.set(v) },
      // Not while Angular is already rendering (the tour can close mid-switch).
      render: () => { try { this.appRef.tick(); } catch { /* it renders on its own */ } },
    });
  }

  private endTour(): void {
    this.tour?.destroy();
    this.tour = null;
  }

  /** The open page's rich Response view, if its endpoint is released;
   *  undefined means the raw JSON. See utils/result-views.ts. */
  viewOf(child: PlaygroundQueryPage): ResultView | undefined {
    return RELEASED_RESULT_VIEWS.has(this.endpointPath()) ? child.resultView : undefined;
  }

  readonly isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

  /** Which tab of the Response card is open; back to Response on every page switch. */
  readonly responseTab = signal<ResponseTab>('response');
  readonly curlCopied = signal(false);

  /** A page opened, or one kept from earlier came back (see
   *  utils/playground-reuse-strategy.ts) with its form and last result. */
  onActivate(component: PlaygroundQueryPage): void {
    this.activeChild = component;
    this.responseTab.set('response');
    this.phoneTab.set('request');
    this.responseUnseen.set(false);
    this.runMenuOpen.set(false);
    // The endpoint row scrolls sideways on phones: bring the open one into view.
    if (this.isPhone()) {
      setTimeout(() => document.querySelector('.endpoint-tab--active')?.scrollIntoView({ inline: 'nearest', block: 'nearest' }));
    }
    // Its run may still be in flight: the loader's timer picks up from its start.
    this.tick();
    // After it renders, so the new form is measured.
    setTimeout(() => this.observeForm());
    // The first endpoint opened walks through its page, 800 ms in (laid out
    // by then, and usually with the endpoint catalog for the first card's
    // description; without it the card uses its own summary), a loader in
    // the middle meanwhile. Each other endpoint's first visit only marks the
    // Tour button (see utils/tour-progress.ts).
    // The page's own route, not endpointPath(): that follows NavigationEnd,
    // which comes after the new page is activated.
    const route = this.outlet()?.isActivated ? this.outlet()!.activatedRoute.snapshot.routeConfig?.path : null;
    const path = route ? `/${route}` : this.endpointPath();
    const arrival = ENDPOINT_INTROS[path] ? this.tourProgress.arriveAt(path) : 'none';
    this.tourNew.set(arrival === 'mark');
    this.tourHint.set(arrival === 'mark');
    if (arrival === 'start') {
      this.tourLoading.set(true);
      setTimeout(() => {
        this.tourLoading.set(false);
        if (this.activeChild === component) this.startTour();
      }, 800);
    }
  }

  updateFormMore(): void {
    const el = this.formScroll()?.nativeElement;
    if (!el) return;
    this.formMore.set(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
    this.formGutter.set(el.offsetWidth - el.clientWidth);
  }

  /** Re-checks when the box or the form inside it changes size (a section
   *  opened, a page switched), not only on scroll. Observes each page's
   *  first element, since the page's own host element is inline. */
  private observeForm(): void {
    const el = this.formScroll()?.nativeElement;
    if (!el || typeof ResizeObserver === 'undefined') return;
    this.formObserver?.disconnect();
    this.formObserver = new ResizeObserver(() => this.updateFormMore());
    this.formObserver.observe(el);
    for (const page of Array.from(el.children)) {
      this.formObserver.observe(page.firstElementChild ?? page);
    }
  }

  setResponseTab(tab: ResponseTab): void {
    this.responseTab.set(tab);
  }

  /** An example question from a result view into the form (not run), shown
   *  where it can be run. */
  tryExample(text: string): void {
    this.activeChild?.useExample?.(text);
    if (this.isPhone()) this.setPhoneTab('request');
  }

  setPhoneTab(tab: PhoneTab): void {
    this.phoneTab.set(tab);
    if (tab === 'response') this.responseUnseen.set(false);
    // Scrolled down past the tabs: the new panel starts right under them,
    // not wherever the last one left the page.
    const tabs = this.phoneTabs()?.nativeElement;
    const area = this.area()?.nativeElement;
    if (tabs && area && area.getBoundingClientRect().top < tabs.getBoundingClientRect().bottom) {
      area.scrollIntoView({ block: 'start' });
    }
  }

  toggleKeyMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.keyMenuOpen.update(v => !v);
  }

  toggleRunMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.runMenuOpen.update(v => !v);
  }

  /** Enough placeholder cards to fill a tall panel; the overflow is clipped. */
  readonly skeletonCards = Array.from({ length: 10 }, (_, i) => i);
  /** Aspect ratios for the /image loader's picture blocks: a fixed mix of
   *  common shapes, as the real ones aren't known until the response. */
  readonly galleryShapes = [1.5, 1, 1.78, 1.33, 0.75, 1.5, 1.33, 1.78, 1, 1.5, 0.8, 1.6, 1.33, 1.5, 1, 1.78, 1.25, 1.5];

  /** Seconds since the open page's run started, for the loading line. */
  readonly elapsedSeconds = signal(0);
  private elapsedTimer: ReturnType<typeof setInterval> | null = null;
  /** When each page's last run started and finished (Date.now()). Per page,
   *  since pages stay alive across endpoint switches and can run at once. */
  private readonly runTimes = new Map<PlaygroundQueryPage, { started: number; finished: number | null }>();
  private readonly now = signal(Date.now());

  /** Run from the footer button or ⌘/Ctrl+Enter: jumps to the Response tab
   *  so the result (or its loading state) is what's on screen. */
  run(child: PlaygroundQueryPage): void {
    if (child.isLoading) return;
    this.responseTab.set('response');
    // The loader starts at the top; a re-run from far down the last result would hide it.
    this.responseBody()?.nativeElement.scrollTo({ top: 0 });
    child.runQuery();
    // Not started (e.g. an empty question): the last result keeps its time,
    // and a phone stays on the form to show what's missing.
    if (!child.isLoading) return;
    if (this.isPhone()) this.setPhoneTab('response');
    this.runTimes.set(child, { started: Date.now(), finished: null });
    this.elapsedSeconds.set(0);
    this.elapsedTimer ??= setInterval(() => this.tick(), 100);
  }

  /** Notes runs that finished (on any page) and updates the open page's
   *  timer; stops once nothing is in flight. */
  private tick(): void {
    const t = Date.now();
    for (const [child, run] of this.runTimes) {
      if (run.finished === null && !child.isLoading) {
        run.finished = t;
        if (child === this.activeChild && this.isPhone() && this.phoneTab() !== 'response') this.responseUnseen.set(true);
      }
    }
    const open = this.activeChild && this.runTimes.get(this.activeChild);
    if (open && open.finished === null) this.elapsedSeconds.set((t - open.started) / 1000);
    if (![...this.runTimes.values()].some(r => r.finished === null)) this.stopElapsed();
  }

  /** "4 min ago" for a result that came in a minute or more ago (e.g. one
   *  you switched back to); null when fresh or not run. */
  finishedAgo(child: PlaygroundQueryPage): string | null {
    const finished = this.runTimes.get(child)?.finished;
    if (!finished) return null;
    const mins = Math.floor((this.now() - finished) / 60_000);
    if (mins < 1) return null;
    return mins < 60 ? `${mins} min ago` : `${Math.floor(mins / 60)} h ago`;
  }

  private stopElapsed(): void {
    if (this.elapsedTimer !== null) {
      clearInterval(this.elapsedTimer);
      this.elapsedTimer = null;
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.tourHint()) {
      this.dismissTourHint();
      return;
    }
    if (event.key !== 'Enter' || !(event.metaKey || event.ctrlKey)) return;
    if (!this.activeChild || this.modalService.hasOpenModal()) return;
    event.preventDefault();
    this.run(this.activeChild);
  }

  /** The Response card's JSON: the API's own `body.data` when the page
   *  keeps it, else the parsed model (pages not migrated yet). */
  rawJson(child: PlaygroundQueryPage): unknown {
    return child.runInfo?.raw ?? child.response;
  }

  headerEntries(child: PlaygroundQueryPage): [string, string][] {
    return Object.entries(child.runInfo?.headers ?? {});
  }

  /** Typed views of `response` for the rich result components. */
  asRag(response: unknown): RagResult {
    return response as RagResult;
  }

  asSearch(response: unknown): SearchResult {
    return response as SearchResult;
  }

  asAnswer(response: unknown): AnswerResult {
    return response as AnswerResult;
  }

  asImage(response: unknown): ImageResult {
    return response as ImageResult;
  }

  copyCurl(curl: string): void {
    navigator.clipboard?.writeText(curl)
      .then(() => {
        this.curlCopied.set(true);
        setTimeout(() => this.curlCopied.set(false), 1500);
      })
      .catch(() => { /* clipboard refused; the command is still selectable in the Headers tab */ });
  }

  onDeactivate(): void {
    this.endTour();
    this.tourHint.set(false);
    this.activeChild = null;
  }

  openResetConfirm(): void {
    this.runMenuOpen.set(false);
    this.modalService.confirm({
      title: 'Reset this query?',
      message: "This clears the question, filters, every other option and the last result.",
      confirmLabel: 'Reset',
      danger: true,
    }).subscribe(confirmed => {
      if (!confirmed || !this.activeChild) return;
      this.activeChild.resetForm();
      this.runTimes.delete(this.activeChild);
    });
  }
}

function endpointPathOf(url: string): string {
  const path = url.split(/[?#]/)[0].replace(/\/+$/, '');
  const last = path.split('/').pop();
  return !last || last === 'playground' ? '/rag' : `/${last}`;
}
